/**
 * AnnotateOverlay — the brightmark point-and-click / text-select capture surface.
 *
 * Renders while annotate mode is active: a subtle page dim (pointer-events:none so it never
 * intercepts the hover/click target resolution) and a fixed instruction toast with a Cancel button.
 * A hover highlighter from `lib/brightmark/hover.ts` outlines the element under the cursor.
 *
 * Capture:
 *  - drag-select text  → `buildTextAnnotation(container, range)` (takes precedence over a click)
 *  - click/tap element → `buildElementAnnotation(el, evt)`
 * Either way it resolves the real page element via `elementFromPoint` (skipping its own UI, tagged
 * `data-brightmark-ui`, and the hover lib's own `data-brightmark-agent` overlay) and calls
 * `onCaptured(annotation, targetElement)`. The screenshot is captured by the parent, off this element.
 *
 * Touch: the listeners are pointer/click based, so a tap resolves exactly like a mouse click —
 * tap-to-pin works unchanged at mobile viewports.
 *
 * Teardown: Esc, or the toast Cancel, calls `onCancel()`; the effect cleanup removes every listener,
 * disables the highlighter, and restores the document cursor. Listeners are attached in the capture
 * phase so they fire before page handlers, and the component is portalled to <body>.
 */
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { BrightmarkAnnotation } from '@/lib/brightmark/types'
import type { BrightmarkLib } from './brightmarkLazy'

const UI_ATTR = 'data-brightmark-ui'

interface AnnotateOverlayProps {
  lib: BrightmarkLib
  onCaptured: (annotation: BrightmarkAnnotation, target: Element) => void
  onCancel: () => void
}

/** True if `el` is part of the annotate UI (this overlay/toast) — excluded from capture resolution. */
function isOwnUi(el: Element | null): boolean {
  return !!el && el.closest(`[${UI_ATTR}]`) !== null
}

export function AnnotateOverlay({ lib, onCaptured, onCancel }: AnnotateOverlayProps) {
  // Keep the latest callbacks without re-running the listener effect (which would re-bind listeners
  // and re-toggle the highlighter on every parent render).
  const onCapturedRef = useRef(onCaptured)
  const onCancelRef = useRef(onCancel)

  useEffect(() => {
    onCapturedRef.current = onCaptured
    onCancelRef.current = onCancel
  })

  useEffect(() => {
    const highlighter = lib.createHoverHighlighter()
    highlighter.enable()

    const prevCursor = document.body.style.cursor
    document.body.style.cursor = 'crosshair'

    // Resolve the real page element under a point, skipping both our UI and the hover overlay.
    const resolveTarget = (x: number, y: number): Element | null => {
      const el = document.elementFromPoint(x, y)
      if (!el || isOwnUi(el) || lib.isAgentUi(el)) return null
      return el
    }

    // Was the last pointer gesture a drag (so the click should be read as a text selection)?
    let dragMoved = false
    let downX = 0
    let downY = 0

    const onPointerDown = (e: PointerEvent): void => {
      if (isOwnUi(e.target as Element | null)) return
      dragMoved = false
      downX = e.clientX
      downY = e.clientY
    }

    const onPointerMove = (e: PointerEvent): void => {
      if (Math.abs(e.clientX - downX) > 4 || Math.abs(e.clientY - downY) > 4) {
        dragMoved = true
      }
    }

    // Prefer a non-collapsed text selection (drag-select) over an element click.
    const tryTextCapture = (): boolean => {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false
      const text = sel.toString().trim()
      if (!text) return false
      const range = sel.getRangeAt(0)
      const raw = range.commonAncestorContainer
      const container = raw.nodeType === Node.ELEMENT_NODE ? (raw as Element) : raw.parentElement
      if (!container || isOwnUi(container) || lib.isAgentUi(container)) return false
      onCapturedRef.current(lib.buildTextAnnotation(container, range), container)
      return true
    }

    const onClick = (e: MouseEvent): void => {
      if (isOwnUi(e.target as Element | null)) return // let the Cancel button work
      e.preventDefault()
      e.stopPropagation()
      if (dragMoved && tryTextCapture()) return
      const target = resolveTarget(e.clientX, e.clientY)
      if (!target) return
      onCapturedRef.current(lib.buildElementAnnotation(target, e), target)
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancelRef.current()
      }
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('pointermove', onPointerMove, true)
    document.addEventListener('click', onClick, true)
    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('pointermove', onPointerMove, true)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('keydown', onKeyDown, true)
      highlighter.disable()
      document.body.style.cursor = prevCursor
    }
  }, [lib])

  return createPortal(
    <>
      {/* Page dim — never intercepts target resolution (pointer-events:none). */}
      <div
        {...{ [UI_ATTR]: 'overlay' }}
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-40 bg-black/20"
      />
      {/* Instruction toast — above the mobile bottom nav on small screens, top-centre otherwise. */}
      <div
        {...{ [UI_ATTR]: 'toast' }}
        role="status"
        data-testid="annotate-toast"
        className="bg-popover text-popover-foreground fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] left-1/2 z-50 flex w-[92vw] -translate-x-1/2 items-center justify-between gap-3 rounded-lg border px-4 py-2.5 shadow-xl sm:top-4 sm:bottom-auto sm:w-auto sm:justify-start"
      >
        <span className="text-sm font-medium">Tap an element, or select text</span>
        <span className="text-muted-foreground hidden text-xs sm:inline">Esc to cancel</span>
        <button
          type="button"
          onClick={onCancel}
          className="hover:bg-muted min-h-[44px] rounded-md border px-3 text-sm font-medium sm:min-h-0 sm:py-1.5"
        >
          Cancel
        </button>
      </div>
    </>,
    document.body
  )
}
